import {t, Trans} from '@lingui/macro';
import React, {FC, useCallback, useEffect, useMemo} from 'react';

import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {PaletteColorsEnum} from '../../../../../libs/shared/ui/src/styles/paletteModel';
import {useLocalize} from '../../../../../srcCommon/internationalization/internationalizationContext';
import {DataFieldsTableColumnId, RDataFieldsTableColumnId} from '../../../../models/api/dataFieldsTableModel';
import {ExpressionRef} from '../../../editors/expressions/expressionRefModel';
import {inferTableColumnExpressionType} from '../../../editors/expressions/infer/getColumnValue';
import {SingleChoiceDropdown} from '../dropdowns/singleChoiceDropdown';
import {ExpressionPill} from '../inputs/expressionPill';
import {defaultDropdownWidth} from '../parameters/genericParameter';
import {RuleMultilineParameterCard} from '../ruleMultilineParameterCard';
import {AtomCardProps} from './atomCard';
import {AtomTitle} from './atomTitle';

const invalidColumnError = t('expression-get-table-column-invalid-column')`Invalid column selected.`;

export const GetTableColumnCard: FC<AtomCardProps> = ({
  atom,
  expressionDependencies,
  isDisabled,
  onUpdateAtom,
  onCancel,
}) => {
  const localize = useLocalize();
  const invalidColumnErrorMessage = useMemo(() => localize(invalidColumnError), [localize]);

  const onChange = useCallback(
    (newColumn: DataFieldsTableColumnId | ExpressionRef | null) => {
      if (!RDataFieldsTableColumnId.is(newColumn)) {
        return;
      }

      onUpdateAtom({...atom, params: {column_id: newColumn}});
    },
    [onUpdateAtom, atom],
  );

  const columns = expressionDependencies?.dataTable?.columns ?? [];
  const selectedColumnId = RDataFieldsTableColumnId.is(atom.params.column_id) ? atom.params.column_id : null;

  useEffect(() => {
    const isValidColumn = expressionDependencies.dataTable?.columns.some(
      (column) => column.id === selectedColumnId,
    );

    const errorMessage = isValidColumn ? undefined : invalidColumnErrorMessage;
    if (atom.errorMessage !== errorMessage) {
      onUpdateAtom({...atom, errorMessage: isValidColumn ? undefined : invalidColumnErrorMessage});
    }
  }, [expressionDependencies.dataTable, selectedColumnId, invalidColumnErrorMessage, atom, onUpdateAtom]);

  return (
    <RuleMultilineParameterCard
      errorMessage={atom.errorMessage}
      icon={<Icon name="tableColumnSmall" />}
      title={
        <AtomTitle
          atom={atom}
          title={<Trans id="rule-expressions-get-column-value-title">Column value in matching row</Trans>}
        />
      }
      onCancel={onCancel}
    >
      <SingleChoiceDropdown
        isDisabled={isDisabled}
        items={columns}
        expressions={null}
        onChange={onChange}
        selectedItem={selectedColumnId}
        getItemKey={(item) => String(item.id)}
        renderItemLabel={(col) => (
          <ExpressionPill
            label={col.name}
            type={{type: inferTableColumnExpressionType(col)}}
            paletteColor={PaletteColorsEnum.BLUE}
          />
        )}
        serializeItemForSearch={(item) => item.name}
        width={defaultDropdownWidth}
        placeholder={<Trans id="rule-editor-enum-param-placeholder">Select a value</Trans>}
      />
    </RuleMultilineParameterCard>
  );
};
